import { useMemo, useState } from 'react';
import { Loader2, Building2, ArrowLeft, Check, Search } from 'lucide-react';
import type { Employer } from '@pm-next/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

interface EmployerSelectProps {
  employers: Employer[];
  onSelect: (employerId: number) => Promise<void>;
  onBack: () => void;
  title?: string;
  description?: string;
  submitLabel?: string;
  backLabel?: string;
  currentEmployerId?: number | null;
}

function matchesSearch(employer: Employer, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    employer.name.toLowerCase().includes(q) ||
    employer.code.toLowerCase().includes(q) ||
    (employer.shortname?.toLowerCase().includes(q) ?? false)
  );
}

export function EmployerSelect({
  employers,
  onSelect,
  onBack,
  title = 'Select Company',
  description = 'Choose a company to continue',
  submitLabel = 'Continue',
  backLabel = 'Back to login',
  currentEmployerId = null,
}: EmployerSelectProps) {
  const [selectedId, setSelectedId] = useState<number | null>(currentEmployerId);
  const [search, setSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const filtered = useMemo(
    () => employers.filter((employer) => matchesSearch(employer, search)),
    [employers, search]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedId === null) return;

    setIsSubmitting(true);
    try {
      await onSelect(selectedId);
      toast({
        title: 'Welcome!',
        description: 'You have been successfully logged in.',
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Selection failed',
        description:
          err instanceof Error ? err.message : 'Failed to select employer. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCardClick = (employerId: number) => {
    if (isSubmitting) return;
    setSelectedId(employerId);
  };

  return (
    <Card className="w-full max-w-5xl">
      <CardHeader className="space-y-1 text-center">
        <CardTitle className="text-2xl font-bold">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="relative mx-auto max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or code…"
              className="pl-9"
              disabled={isSubmitting}
              aria-label="Search companies"
            />
          </div>

          {filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No companies match “{search.trim()}”.
            </p>
          ) : (
            <div className="grid max-h-[min(24rem,50vh)] grid-cols-2 gap-4 overflow-y-auto sm:grid-cols-3 lg:grid-cols-4">
              {filtered.map((employer) => {
                const isSelected = selectedId === employer.id;
                return (
                  <button
                    key={employer.id}
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => handleCardClick(employer.id)}
                    className={cn(
                      'relative flex flex-col items-center gap-3 rounded-lg border-2 p-4 text-center transition-all hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-muted-foreground/25',
                      isSubmitting && 'cursor-not-allowed opacity-50'
                    )}
                  >
                    {isSelected && (
                      <div className="absolute right-2 top-2 rounded-full bg-primary p-0.5">
                        <Check className="h-3 w-3 text-primary-foreground" />
                      </div>
                    )}
                    <div
                      className={cn(
                        'flex h-12 w-12 items-center justify-center rounded-full',
                        isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                      )}
                    >
                      <Building2 className="h-6 w-6" />
                    </div>
                    <div className="space-y-1">
                      <div className="font-medium leading-tight">{employer.name}</div>
                      <div className="text-xs text-muted-foreground">{employer.code}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="space-y-2">
            <Button type="submit" className="w-full" disabled={isSubmitting || selectedId === null}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                submitLabel
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={onBack}
              disabled={isSubmitting}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {backLabel}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
